import { HistoryButton, useDocEvents } from "../team/collab.jsx";
const { useEffect, useRef, useState } = React;
import { dbGetMaster, dbListTemplates, dbSetMaster } from "../data.jsx";
import { BoardEditor } from "../editor/BoardEditor.jsx";
import { useT } from "../i18n.jsx";
import { WEEKDAYS, emptyBoard, rwsMasterFromSep16, withBoardDefaults } from "../model.jsx";
import { Loader2, Save, StarIcon, T } from "../theme.jsx";

// =============================================================== MASTER =====

function MasterScreen({ perms }) {
  const { lang, t } = useT();
  const [wdId, setWdId] = useState("sun");
  const [board, setBoard] = useState(emptyBoard());
  const [loadedWd, setLoadedWd] = useState(null);
  // right after switching weekday, `board` still holds the previous day until
  // its load finishes; editing it then would save that day's content over
  // this one, so nothing is shown (or editable) until this weekday is loaded
  const loading = loadedWd !== wdId;
  const [saveState, setSaveState] = useState("saved");
  const saveTimer = useRef(null);
  const [reloadTick, setReloadTick] = useState(0);
  const wd = WEEKDAYS.find((w) => w.id === wdId);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { dbListTemplates().then(setTemplates); }, []);
  const linkedId = board.templateId || "";
  const linkedTpl = templates.find((x) => x.id === linkedId);
  const setLink = (id) => {
    const next = { ...board };
    if (id) next.templateId = id; else delete next.templateId;
    handleChange(next);
  };
  useDocEvents("masters", wdId, {
    onReload: () => setReloadTick((n) => n + 1),
    onSaved: () => setSaveState("saved"),
    onConflict: () => { clearTimeout(saveTimer.current); setSaveState("conflict"); },
  });

  useEffect(() => {
    let cancelled = false;
    setLoadedWd(null);
    (async () => {
      const v = await dbGetMaster(wdId);
      if (cancelled) return;
      if (v) {
        setBoard(withBoardDefaults(v));
      } else if (wdId === "sat") {
        // fallback shown client-side only — NOT written to storage here.
        // it only gets persisted once someone actually edits it, so a flaky
        // write here can't keep retrying every time this screen mounts.
        setBoard(rwsMasterFromSep16());
      } else {
        setBoard({ ...emptyBoard(), name: wd.name, category: wd.category, code: wd.code });
      }
      setSaveState("saved");
      setLoadedWd(wdId);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wdId, reloadTick]);

  const handleChange = (next) => {
    setBoard(next);
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const ok = await dbSetMaster(wdId, next);
      setSaveState((s) => (ok ? "saved" : s === "conflict" ? s : "error"));
    }, 600);
  };

  return (
    <>
      <div className="rb-scroll" style={{ display: "flex", gap: 6, overflowX: "auto", padding: "10px 16px", background: T.panel, borderBottom: `1px solid ${T.border}`, justifyContent: "center" }}>
        {WEEKDAYS.map((w) => (
          <button key={w.id} className="rb-btn" onClick={() => setWdId(w.id)}
            style={{ flex: "0 0 auto", padding: "8px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: wdId === w.id ? T.accent : T.panel2, color: wdId === w.id ? T.accentText : T.text, cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{lang === "en" ? w.labelEn : w.label}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>{w.code}</div>
          </button>
        ))}
      </div>
      <div style={{ padding: "8px 16px", background: T.panel, borderBottom: `1px solid ${T.border}`, fontSize: 11.5, color: T.subtext, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textAlign: "center", flexWrap: "wrap" }}>
        <StarIcon size={12} color="#FFD60A" /> {t("masterEditPrefix")} {lang === "en" ? wd.labelEn : wd.label} {t("masterEditMid")} {lang === "en" ? wd.labelEn : wd.label} {t("masterEditSuffixPart1")} <b>{t("masterEditSuffixPart2")}</b> {t("masterEditSuffixPart3")}
        <span role="status" style={{ display: "flex", alignItems: "center", gap: 5, color: saveState === "error" || saveState === "conflict" ? T.danger : T.subtext }}>
          {saveState === "saving" ? <><Loader2 size={12} style={{ animation: "rb-spin 1s linear infinite" }} /> {t("saving")}</> : saveState === "conflict" ? t("saveConflict") : saveState === "error" ? t("saveFailed") : <><Save size={12} /> {t("saved")}</>}
        </span>
        <HistoryButton col="masters" id={wdId} perms={perms} />
      </div>
      {!loading && (
        <div style={{ padding: "8px 16px", background: linkedId ? T.infoBg : T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", textAlign: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{t("weekdayTplLabel")} {lang === "en" ? wd.labelEn : wd.label}</span>
          <select aria-label={t("weekdayTplLabel")} value={linkedId} disabled={!perms.canMaster} onChange={(e) => setLink(e.target.value)}
            style={{ fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", background: T.panel2, color: T.text }}>
            <option value="">{t("weekdayTplNone")}</option>
            {templates.map((x) => <option key={x.id} value={x.id}>{x.name || x.id}</option>)}
            {linkedId && !linkedTpl && <option value={linkedId}>{t("weekdayTplMissing")}</option>}
          </select>
        </div>
      )}
      {loading ? <div style={{ padding: 50, textAlign: "center", color: T.subtext }}>{t("loading")}</div> :
        linkedId ? (
          <div style={{ padding: 40, textAlign: "center", color: T.subtext, fontSize: 13, lineHeight: 1.7 }}>
            {t("weekdayTplOnPrefix")} {lang === "en" ? wd.labelEn : wd.label} {t("weekdayTplOnMid")} <b style={{ color: T.text }}>{linkedTpl ? linkedTpl.name : linkedId}</b>
            <br />{t("weekdayTplOnHint")}
          </div>
        ) :
        <BoardEditor key={wdId} board={board} onChange={handleChange} perms={perms} showMeta />}
    </>
  );
}

export { MasterScreen };
